import * as Immutable from 'immutable';
import { PolygonalHotspotDrawer } from './PolygonalHotspotDrawer';
import { embedPanoPromised, KrpanoWrapper } from './krpano';
import { GLOBAL_PANO_DIV_ID } from '../public/types/consts';
import { HotspotStyle } from '../public/types/HotspotStyle';
import { LayerStyle } from '../public/types/LayerStyle';
import { AssertionException } from './utils/Exceptions';
import he from 'he';
import { createPolygonalHotspot } from '../public/v4/krpano/functions/krpanoPolygonalTypeFunctions';
import { ArrowPosition } from '../../panorama/Positions';
import { FriendlyNameService } from '../public/types/PanoramaArrowIdentifier';
export const EDITED_ARROW_NAME = 'new_hotspot';
export class Panorama {
    /**
     * Creates panorama and prepares it for manipulation via onReady event.
     **/
    static async CreatePanorama(skinXmlPath, viewerId, embedpanoFunc, panoramaData, targetId, sceneIndex) {
        const krpano = await embedPanoPromised({
            xml: skinXmlPath,
            target: targetId !== null && targetId !== void 0 ? targetId : GLOBAL_PANO_DIV_ID,
            id: viewerId,
            html5: 'only',
            mobilescale: 1,
            webglsettings: { preserveDrawingBuffer: false, depth: false, stencil: false, antialias: false },
        }, embedpanoFunc);
        const pano = new KrpanoWrapper(krpano);
        // Prepare panorama instance to be used
        const createdPano = new Panorama(pano);
        // Panorama is really ready to be used when this 'pano loading' is finished
        // because initialy we just pass the 'skin.xml' which does not contain any
        // pano images just styles to be used later by the real pano
        // Thus the panorama with images is really ready only after we load a real pano
        if (panoramaData) {
            await pano.loadPanoFromXmlString(panoramaData);
            pano.loadScene(sceneIndex !== null && sceneIndex !== void 0 ? sceneIndex : panoramaData.startingSceneIndex);
        }
        else {
            await pano.loadEmptyPano();
        }
        return createdPano;
    }
    constructor(_pano) {
        this._pano = _pano;
        this._displayedHotspots = Immutable.Set(); //does this has purpose still, since PanoV2 has arrow register??
        this._displayedLayers = Immutable.Set();
        this._polygonalDrawers = new Map();
        this.dispose = () => {
            this.unregisterOnClick();
            this.unregisterOnSceneChange();
            this._pano.unload();
        };
        this.registerOnClick = (handler) => this._pano.registerOnClick(handler);
        this.unregisterOnClick = () => this._pano.unregisterOnClick();
        this.registerOnSceneChange = (handler) => {
            this._pano.registerOnNewScene(handler);
        };
        this.unregisterOnSceneChange = () => {
            this._pano.unregisterOnNewScene();
        };
        /**
         * Returns a drawer for a polygonal arrow.
         * The same drawer is returned for given @param {arrowNameIdentifier}.
         */
        this.getPolygonalDrawer = (arrowNameIdentifier) => {
            if (!this._polygonalDrawers.has(arrowNameIdentifier)) {
                this._polygonalDrawers.set(arrowNameIdentifier, new PolygonalHotspotDrawer(arrowNameIdentifier, this));
            }
            const drawer = this._polygonalDrawers.get(arrowNameIdentifier);
            if (drawer) {
                return drawer;
            }
            throw AssertionException('This code should be unreachable as Drawer must always exist here.');
        };
        this.getMinFov = () => this._pano.getMinFov();
        this.getMaxFov = () => this._pano.getMaxFov();
        this.getAverageFov = () => this._pano.getAverageFov();
        this._getSimpleArrowLayerName = (arrowIdentifier) => `${arrowIdentifier}Layer`;
        this._registerHotspot = (newHotspotName) => {
            this._displayedHotspots = this._displayedHotspots.add(newHotspotName);
        };
        this.lookToCameraPosition = (cameraPosition, lookToQuick) => this._pano.lookTo(cameraPosition, lookToQuick);
        this.lookToSimpleArrow = (simpleArrow) => {
            const lookTo = {
                fov: simpleArrow.fov,
                xAxis: simpleArrow.arrowPositionConfiguration.arrowPosition.xAxis,
                yAxis: simpleArrow.arrowPositionConfiguration.arrowPosition.yAxis,
                sceneIndex: simpleArrow.sceneIndex,
            };
            this._pano.lookTo(lookTo);
        };
        this.renderPolygonalHotspot = (renderer) => {
            const hotspotName = renderer.getName();
            renderer.render({
                setHotspotPointPosition: (name, pointIndex, x, y) => {
                    this._pano.setPointXPosition(name, pointIndex, x);
                    this._pano.setPointYPosition(name, pointIndex, y);
                },
                addIndicator: (name, x, y, scale, onClick) => {
                    this.addIndicator(x, y, scale, name, onClick);
                },
                addHotspot: this.addHotspot,
                addHotspotOnClickHandler: this.addHotspotOnClickHandler,
                disableHotspot: this._pano.disableHotspot,
            });
            this._registerHotspot(hotspotName);
        };
        //possible problem from, using directly not through PanoV2
        this.addHotspot = (hotspotName, preventRegistration = false) => {
            const hotspot = this._pano.addHotspot(hotspotName);
            if (preventRegistration) {
                return hotspot;
            }
            this._registerHotspot(hotspotName);
            return hotspot;
        };
        this.addHotspotOnClickHandler = (hotspotName, onClickHandler) => this._pano.registerHotspotOnClick(hotspotName, onClickHandler);
        //possible problem from, using directly not through PanoV2
        this.removeHotspot = (hotspotName) => {
            this._pano.call(`removehotspot(${hotspotName});`);
            this.removePolygonalArrowIndicator(hotspotName);
        };
        this.removePolygonalArrowIndicator = (hotspotName) => {
            this._pano.removePolygonalArrowIndicator(hotspotName);
        };
        this.removeLayer = (layerName) => {
            this._pano.call(`removelayer(${layerName});`);
        };
        this.removeArrow = (arrowNameIdentifier) => {
            this.removeHotspot(arrowNameIdentifier);
            this.removePolygonalArrowIndicator(arrowNameIdentifier);
            // This is usefull only when arrow was simple before
            this.removeLayer(this._getSimpleArrowLayerName(arrowNameIdentifier));
        };
    }
    // adds a simple hotspot hotspot (one point) to panorama.
    addSimpleHotspot(arrowText, x, y, layerName = 'layer', hotspotName = 'hotspot', onClickHandler, layerClass, layerStyle, delayedDestroy, hotspotEnabled = true) {
        const hotspot = this.addHotspot(hotspotName, !!delayedDestroy);
        hotspot.ath = x;
        hotspot.atv = y;
        hotspot.loadstyle(HotspotStyle.HotspotWithText);
        hotspot.enabled = hotspotEnabled;
        hotspot.visible = true;
        hotspot.onclick = onClickHandler;
        const textLayer = this._pano.addLayer(layerName);
        textLayer.loadstyle(layerStyle ? layerStyle : LayerStyle.LayerText);
        textLayer.parent = `hotspot[${hotspotName}]`;
        textLayer.handcursor = true;
        textLayer.html = `<p class='${layerClass ? layerClass : 'htext'}'>${he.encode(arrowText)}<p>`;
        if (!delayedDestroy) {
            this._displayedLayers = this._displayedLayers.add(layerName);
        }
        else {
            // This actually means it is just timed hotspot (success fail in quiz)
            this._pano.call(`stopdelayedcall(${hotspotName}_delayed1);stopdelayedcall(${hotspotName}_delayed2);stoptween(layer[${layerName}].alpha);set(layer[${layerName}].alpha,1);`);
            this._pano.call(`delayedcall(${hotspotName}_delayed1,${delayedDestroy},tween(layer[${layerName}].alpha , 0 , 1 ); );`);
            this._pano.call(`delayedcall(${hotspotName}_delayed2,${delayedDestroy + 1},removelayer(${layerName});removehotspot(${hotspotName}));`);
        }
    }
    getCenterOfPolygon(points) {
        const temporaryName = FriendlyNameService.generateRandomId();
        const temp = createPolygonalHotspot(points, this._pano, temporaryName, (h) => (h.visible = false));
        const { x, y } = temp.getcenter();
        this.removeHotspot(temporaryName);
        return new ArrowPosition(x, y);
    }
    addIndicator(xAxis, yAxis, indicatorScale, newHotspotName, onClickHandler) {
        const indicatorHotspot = this.addHotspot(newHotspotName);
        indicatorHotspot.ath = xAxis;
        indicatorHotspot.atv = yAxis;
        indicatorHotspot.scale = indicatorScale;
        indicatorHotspot.onclick = onClickHandler;
        indicatorHotspot.loadstyle(HotspotStyle.HotspotIndicator);
    }
}
